// ABOUTME: Order Change Lead Time form — manages per-field editability and auto-approval via toggle buttons.
// ABOUTME: Renders "Editable fields", "Auto-approval fields", and "Slot management fields" sections with per-field lead time inputs.

import {Button, Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiCheck, mdiInformationOutline, mdiPlus} from '@mdi/js'
import React, {useCallback, useEffect, useMemo} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {DEFAULT_LEAD_TIME, OrderChangeLeadTimeConfig, OrderChangeLeadTimeFormValues, OrderChangeFieldType, OrderChangeFieldFormValues, normalizeFieldCode} from '../types/orderChangeLeadTime'
import {TIME_UOM_OPTIONS, REFERENCE_EVENTS} from '../types/orderIntakeLeadTime'
import {FilterDropdown} from './FilterDropdown'
import {NumericalInput} from './NumericalInput'
import {SectionTitle} from './SectionTitle'

export interface OrderChangeLeadTimeFormProps {
  config: OrderChangeLeadTimeConfig
  availableFields?: string[]
  isReadOnly: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: OrderChangeLeadTimeFormValues) => void
}

const DEFAULT_FIELD: Omit<OrderChangeFieldFormValues, 'fieldCode'> = {
  type: 'notChangeable' as OrderChangeFieldType,
  autoApproveLeadTime: {...DEFAULT_LEAD_TIME},
  orderManagementLeadTime: {...DEFAULT_LEAD_TIME},
}

const toFormValues = (config: OrderChangeLeadTimeConfig, availableFields?: string[]): OrderChangeLeadTimeFormValues => {
  if (!availableFields || availableFields.length === 0) {
    return {configurations: config.configurations}
  }
  const configMap = new Map(config.configurations.map((c) => [c.fieldCode, c]))
  return {
    configurations: availableFields.map((fieldCode) =>
      configMap.get(fieldCode) ?? {fieldCode, ...DEFAULT_FIELD}
    ),
  }
}

/** Translation key prefix for field labels */
const FIELD_LABEL_PREFIX = 'countryConfigurations.orderChangeLT.fields'

export const OrderChangeLeadTimeForm: React.FC<OrderChangeLeadTimeFormProps> = ({
  config,
  availableFields,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
}) => {
  const {t} = useTranslation()
  const formDefaultValues = useMemo(() => toFormValues(config, availableFields), [config, availableFields])
  const {control, setValue, reset, formState: {isDirty}} = useForm<OrderChangeLeadTimeFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const configurations = useWatch({control, name: 'configurations'})

  const values: OrderChangeLeadTimeFormValues = useMemo(
    () => ({configurations}),
    [configurations]
  )

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const isValid = useMemo(() => {
    const isLeadTimeValid = (lt?: {value: number; uomId: string} | null) =>
      !lt || (lt.value >= 0 && Boolean(lt.uomId))
    return configurations.length > 0 && configurations.every((field) =>
      field.type === 'notChangeable' ||
      (isLeadTimeValid(field.autoApproveLeadTime) && isLeadTimeValid(field.orderManagementLeadTime)))
  }, [configurations])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    onValuesChange?.(values)
  }, [values, onValuesChange])

  const handleToggle = useCallback((index: number) => {
    if (isReadOnly) return
    const current = configurations[index]
    const newType: OrderChangeFieldType = current.type === 'notChangeable' ? 'autoApproval' : 'notChangeable'
    setValue(`configurations.${index}.type`, newType, {shouldDirty: true})
  }, [configurations, isReadOnly, setValue])

  const handleAutoApprovalToggle = useCallback((index: number) => {
    if (isReadOnly) return
    const current = configurations[index]
    if (current.type === 'notChangeable') return
    const newType: OrderChangeFieldType = current.type === 'autoApproval' ? 'slotManagementApproval' : 'autoApproval'
    setValue(`configurations.${index}.type`, newType, {shouldDirty: true})
  }, [configurations, isReadOnly, setValue])

  const referenceEventOptions = useMemo(
    () => REFERENCE_EVENTS.map((ev) => ({
      value: ev,
      label: t(`countryConfigurations.leadTimes.referenceEvents.${ev}`),
    })),
    [t],
  )

  return (
    <div className="flex flex-col gap-6 p-6 w-full">
      <SectionTitle titleKey="countryConfigurations.orderChangeLT.editableFields.title" />

      <div className="flex flex-wrap gap-2">
        {configurations.map((field, index) => {
          const isEditable = field.type !== 'notChangeable'
          const fieldCodeLower = normalizeFieldCode(field.fieldCode)
          return (
            <Button
              key={field.fieldCode}
              variant="secondary"
              size="small"
              toggled={isEditable}
              colorClass={isEditable ? 'bg-foreground text-foreground_inverted border-1px border-transparent' : undefined}
              iconPrefix={isEditable ? mdiCheck : mdiPlus}
              onClick={() => handleToggle(index)}
              disabled={isReadOnly}
              data-cy={`pill-${fieldCodeLower}`}
            >
              {t(`${FIELD_LABEL_PREFIX}.${fieldCodeLower}`, field.fieldCode)}
            </Button>
          )
        })}
      </div>

      {/* Section separator */}
      <hr className="border-border" />

      {/* Auto-approval fields section */}
      <SectionTitle
        titleKey="countryConfigurations.orderChangeLT.autoApproval.title"
        subtitle="countryConfigurations.orderChangeLT.autoApproval.subtitle"
      />

      <div className="flex flex-wrap gap-2">
        {configurations.map((field, index) => {
          const isNotChangeable = field.type === 'notChangeable'
          const isAutoApproval = field.type === 'autoApproval'
          const fieldCodeLower = normalizeFieldCode(field.fieldCode)
          const pill = (
            <Button
              key={field.fieldCode}
              variant="secondary"
              size="small"
              toggled={isAutoApproval}
              colorClass={isAutoApproval ? 'bg-foreground text-foreground_inverted border-1px border-transparent' : undefined}
              iconPrefix={isAutoApproval ? mdiCheck : mdiPlus}
              onClick={() => handleAutoApprovalToggle(index)}
              disabled={isReadOnly || isNotChangeable}
              data-cy={`auto-approval-pill-${fieldCodeLower}`}
            >
              {t(`${FIELD_LABEL_PREFIX}.${fieldCodeLower}`, field.fieldCode)}
            </Button>
          )
          if (isNotChangeable && !isReadOnly) {
            return (
              <CountryConfigsTooltip key={field.fieldCode} content={t('countryConfigurations.orderChangeLT.editableFields.notEditableTooltip')} delayMs={300}>
                <span>{pill}</span>
              </CountryConfigsTooltip>
            )
          }
          return pill
        })}
      </div>

      {/* Per-field lead time rows for auto-approval fields */}
      {configurations.map((field, index) => {
        if (field.type !== 'autoApproval') return null
        const fieldCodeLower = normalizeFieldCode(field.fieldCode)
        const fieldLabel = t(`${FIELD_LABEL_PREFIX}.${fieldCodeLower}`, field.fieldCode)
        return (
          <div key={`lt-${field.fieldCode}`} className="flex flex-col gap-4">
            <TextMedium className="text-foreground font-semibold">{fieldLabel}</TextMedium>

            {/* Auto-approval lead time row */}
            <div className="flex gap-4 items-start w-full">
              <div className="flex flex-col justify-center shrink-0 h-10 w-[220px]">
                <div className="flex gap-0.5 items-center whitespace-nowrap">
                  <TextMedium className="text-foreground line-clamp-1">
                    {t('countryConfigurations.orderChangeLT.autoApproval.autoApprovalLeadTime')}
                  </TextMedium>
                  <CountryConfigsTooltip content={t('countryConfigurations.orderChangeLT.autoApproval.autoApprovalLeadTimeTooltip')} delayMs={300}>
                    <span className="text-foreground flex items-center cursor-default">
                      <Icon path={mdiInformationOutline} size="regular" />
                    </span>
                  </CountryConfigsTooltip>
                </div>
              </div>
              <div className="grid grid-cols-[auto_auto_1fr] gap-1 items-start flex-1">
              <NumericalInput
                value={field.autoApproveLeadTime.value}
                onChange={(val) => setValue(`configurations.${index}.autoApproveLeadTime.value`, val, {shouldDirty: true})}
                min={0}
                disabled={isReadOnly}
                data-cy={`auto-approval-lt-value-${fieldCodeLower}`}
              />
              <FilterDropdown
                value={field.autoApproveLeadTime.uomId}
                onChange={(val) => {
                  const opt = TIME_UOM_OPTIONS.find((o) => o.value === val)
                  setValue(`configurations.${index}.autoApproveLeadTime.uomId`, val, {shouldDirty: true})
                  setValue(`configurations.${index}.autoApproveLeadTime.uomDisplay`, opt?.label ?? val, {shouldDirty: true})
                }}
                options={TIME_UOM_OPTIONS}
                size="small"
                data-cy={`auto-approval-lt-uom-${fieldCodeLower}`}
              />
              <FilterDropdown
                value={field.autoApproveLeadTime.referenceEvent}
                onChange={(val) => setValue(`configurations.${index}.autoApproveLeadTime.referenceEvent`, val, {shouldDirty: true})}
                options={referenceEventOptions}
                size="medium"
                data-cy={`auto-approval-lt-ref-${fieldCodeLower}`}
              />
              </div>
            </div>

            {/* Order mgmt lead time row */}
            <div className="flex gap-4 items-start w-full">
              <div className="flex flex-col justify-center shrink-0 h-10 w-[220px]">
                <div className="flex gap-0.5 items-center whitespace-nowrap">
                  <TextMedium className="text-foreground line-clamp-1">
                    {t('countryConfigurations.orderChangeLT.autoApproval.orderMgmtLeadTime')}
                  </TextMedium>
                  <CountryConfigsTooltip content={t('countryConfigurations.orderChangeLT.autoApproval.orderMgmtLeadTimeTooltip')} delayMs={300}>
                    <span className="text-foreground flex items-center cursor-default">
                      <Icon path={mdiInformationOutline} size="regular" />
                    </span>
                  </CountryConfigsTooltip>
                </div>
              </div>
              <div className="grid grid-cols-[auto_auto_1fr] gap-1 items-start flex-1">
              <NumericalInput
                value={field.orderManagementLeadTime.value}
                onChange={(val) => setValue(`configurations.${index}.orderManagementLeadTime.value`, val, {shouldDirty: true})}
                min={0}
                disabled={isReadOnly}
                data-cy={`order-mgmt-lt-value-${fieldCodeLower}`}
              />
              <FilterDropdown
                value={field.orderManagementLeadTime.uomId}
                onChange={(val) => {
                  const opt = TIME_UOM_OPTIONS.find((o) => o.value === val)
                  setValue(`configurations.${index}.orderManagementLeadTime.uomId`, val, {shouldDirty: true})
                  setValue(`configurations.${index}.orderManagementLeadTime.uomDisplay`, opt?.label ?? val, {shouldDirty: true})
                }}
                options={TIME_UOM_OPTIONS}
                size="small"
                data-cy={`order-mgmt-lt-uom-${fieldCodeLower}`}
              />
              <FilterDropdown
                value={field.orderManagementLeadTime.referenceEvent}
                onChange={(val) => setValue(`configurations.${index}.orderManagementLeadTime.referenceEvent`, val, {shouldDirty: true})}
                options={referenceEventOptions}
                size="medium"
                data-cy={`order-mgmt-lt-ref-${fieldCodeLower}`}
              />
              </div>
            </div>
          </div>
        )
      })}

      {/* Slot management section — only visible when at least one field is 'slotManagementApproval' */}
      {configurations.some((f) => f.type === 'slotManagementApproval') && (
        <>
          {/* Section separator */}
          <hr className="border-border" />

          {/* Slot management fields section */}
          <SectionTitle
            titleKey="countryConfigurations.orderChangeLT.slotManagement.title"
            subtitle="countryConfigurations.orderChangeLT.slotManagement.subtitle"
          />

          {/* Per-field lead time rows for slot management fields */}
          {configurations.map((field, index) => {
            if (field.type !== 'slotManagementApproval') return null
            const fieldCodeLower = normalizeFieldCode(field.fieldCode)
            const fieldLabel = t(`${FIELD_LABEL_PREFIX}.${fieldCodeLower}`, field.fieldCode)
            return (
              <div key={`slot-${field.fieldCode}`} className="flex flex-col gap-4">
                <TextMedium className="text-foreground font-semibold">{fieldLabel}</TextMedium>

                {/* Slot mgmt lead time row */}
                <div className="flex gap-4 items-start w-full">
                  <div className="flex flex-col justify-center shrink-0 h-10 w-[220px]">
                    <div className="flex gap-0.5 items-center whitespace-nowrap">
                      <TextMedium className="text-foreground line-clamp-1">
                        {t('countryConfigurations.orderChangeLT.slotManagement.slotMgmtLeadTime')}
                      </TextMedium>
                      <CountryConfigsTooltip content={t('countryConfigurations.orderChangeLT.slotManagement.slotMgmtLeadTimeTooltip')} delayMs={300}>
                        <span className="text-foreground flex items-center cursor-default">
                          <Icon path={mdiInformationOutline} size="regular" />
                        </span>
                      </CountryConfigsTooltip>
                    </div>
                  </div>
                  <div className="grid grid-cols-[auto_auto_1fr] gap-1 items-start flex-1">
                  <NumericalInput
                    value={field.autoApproveLeadTime.value}
                    onChange={(val) => setValue(`configurations.${index}.autoApproveLeadTime.value`, val, {shouldDirty: true})}
                    min={0}
                    disabled={isReadOnly}
                    data-cy={`slot-mgmt-lt-value-${fieldCodeLower}`}
                  />
                  <FilterDropdown
                    value={field.autoApproveLeadTime.uomId}
                    onChange={(val) => {
                      const opt = TIME_UOM_OPTIONS.find((o) => o.value === val)
                      setValue(`configurations.${index}.autoApproveLeadTime.uomId`, val, {shouldDirty: true})
                      setValue(`configurations.${index}.autoApproveLeadTime.uomDisplay`, opt?.label ?? val, {shouldDirty: true})
                    }}
                    options={TIME_UOM_OPTIONS}
                    size="small"
                    data-cy={`slot-mgmt-lt-uom-${fieldCodeLower}`}
                  />
                  <FilterDropdown
                    value={field.autoApproveLeadTime.referenceEvent}
                    onChange={(val) => setValue(`configurations.${index}.autoApproveLeadTime.referenceEvent`, val, {shouldDirty: true})}
                    options={referenceEventOptions}
                    size="medium"
                    data-cy={`slot-mgmt-lt-ref-${fieldCodeLower}`}
                  />
                  </div>
                </div>

                {/* Order mgmt lead time row */}
                <div className="flex gap-4 items-start w-full">
                  <div className="flex flex-col justify-center shrink-0 h-10 w-[220px]">
                    <div className="flex gap-0.5 items-center whitespace-nowrap">
                      <TextMedium className="text-foreground line-clamp-1">
                        {t('countryConfigurations.orderChangeLT.slotManagement.orderMgmtLeadTime')}
                      </TextMedium>
                      <CountryConfigsTooltip content={t('countryConfigurations.orderChangeLT.slotManagement.orderMgmtLeadTimeTooltip')} delayMs={300}>
                        <span className="text-foreground flex items-center cursor-default">
                          <Icon path={mdiInformationOutline} size="regular" />
                        </span>
                      </CountryConfigsTooltip>
                    </div>
                  </div>
                  <div className="grid grid-cols-[auto_auto_1fr] gap-1 items-start flex-1">
                  <NumericalInput
                    value={field.orderManagementLeadTime.value}
                    onChange={(val) => setValue(`configurations.${index}.orderManagementLeadTime.value`, val, {shouldDirty: true})}
                    min={0}
                    disabled={isReadOnly}
                    data-cy={`slot-order-mgmt-lt-value-${fieldCodeLower}`}
                  />
                  <FilterDropdown
                    value={field.orderManagementLeadTime.uomId}
                    onChange={(val) => {
                      const opt = TIME_UOM_OPTIONS.find((o) => o.value === val)
                      setValue(`configurations.${index}.orderManagementLeadTime.uomId`, val, {shouldDirty: true})
                      setValue(`configurations.${index}.orderManagementLeadTime.uomDisplay`, opt?.label ?? val, {shouldDirty: true})
                    }}
                    options={TIME_UOM_OPTIONS}
                    size="small"
                    data-cy={`slot-order-mgmt-lt-uom-${fieldCodeLower}`}
                  />
                  <FilterDropdown
                    value={field.orderManagementLeadTime.referenceEvent}
                    onChange={(val) => setValue(`configurations.${index}.orderManagementLeadTime.referenceEvent`, val, {shouldDirty: true})}
                    options={referenceEventOptions}
                    size="medium"
                    data-cy={`slot-order-mgmt-lt-ref-${fieldCodeLower}`}
                  />
                  </div>
                </div>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
